import { useEffect, useState } from "react";
import { PMT, IRR, IPMT } from "../helpers/calculation.helpers";
import { loanCalculationRuleSet } from "./LoanCalculator.config";

export const useLoanCalculatorController = ({
  initialLoanAmount,
  initialLoanYears,
  initialInterestRate,
  initialPaymentFee,
  initialEstablishmentFee,
  initialNumberOfYearlyPayments,
  initialLoanType,
  minInterestRate,
  maxInterestRate,
  version,
}) => {
  const calculationRules = loanCalculationRuleSet[version]; //Lang Specific
  const [loanType, setLoanType] = useState(initialLoanType);
  const [amount, setAmount] = useState(initialLoanAmount);
  const [months, setMonths] = useState(initialLoanYears);
  const [interestRate, setInterestRate] = useState(initialInterestRate);
  const [paymentFee, setPaymentFee] = useState(calculationRules[0].InvoiceFee);


  const [establishmentFee, setEstablishmentFee] = useState(
    initialEstablishmentFee
  );
   
  const [numberOfYearlyPayments, setNumberOfYearlyPayments] = useState(
    initialNumberOfYearlyPayments
  );
  const [minLoanAmount, setMinLoanAmount] = useState(0);
  const [maxLoanAmount, setMaxLoanAmount] = useState(0);
  const [maxLoanMonths, setMaxLoanMonths] = useState(0);
  const minLoanYears = 1;

  useEffect(() => {
    setMinLoanAmount(calculationRules[0].from);
    setMaxLoanAmount(calculationRules[calculationRules.length - 1].to);
  }, [calculationRules]);


  useEffect(() => {
    const rule = calculationRules.filter(
      (rule) => amount >= rule.from && amount <= rule.to
    )[0];

    if (rule) {
      setMaxLoanMonths(rule.maxMonths);
      
      if (months > rule.maxMonths) {
        setMonths(rule.maxMonths);
      }
    }

    if (maxLoanAmount !== 0 && minLoanAmount !== 0) {
      if (amount > maxLoanAmount) {
        setAmount(maxLoanAmount);
      } else if (amount < minLoanAmount) {
        setAmount(minLoanAmount);
      }
    }
  }, [amount, calculationRules]);

  useEffect(() => {
    if (maxLoanMonths !== 0) {
      if (months > maxLoanMonths) {
        setMonths(maxLoanMonths);
        console.log("months > maxLoanMonths TRUE" + maxLoanMonths);
      } else if (months < minLoanYears) {
        console.log("months < minLoanYears TRUE" + maxLoanMonths);
        setMonths(minLoanYears);
      }
    }
    console.log(months);
  }, [months]);

  useEffect(() => {
    if (interestRate > maxInterestRate) {
      setInterestRate(maxInterestRate);
    } else if (interestRate < minInterestRate) {
      setInterestRate(minInterestRate);
    }
  }, [interestRate]);

  return {
    loanType,
    setLoanType,
    amount,
    setAmount,
    years: months,
    setYears: setMonths,
    interestRate,
    setInterestRate,
    paymentFee,
    setPaymentFee,
    establishmentFee,
    setEstablishmentFee,
    numberOfYearlyPayments,
    setNumberOfYearlyPayments,
    minLoanAmount,
    maxLoanAmount,
    minLoanYears,
    maxLoanYears: maxLoanMonths,
  };
};

export const LoanTypes = {
  Annuity: "Annuity",
  Serial: "Serial",
};

export const useCalculatedLoan = ({
  loanType,
  amount,
  months,
  interestRate,
  establishmentFee,
  paymentFee,
  numberOfYearlyPayments,
}) => {
  
  const yearlyInterestRateDecimal = interestRate / 100;
  console.log(
    "years:" +
      months +
      "ypayments:" +
      numberOfYearlyPayments +
      "est fee: " +
      establishmentFee
  );
  const numberOfRepayments = months //months;
  const nominalInterestRate =
    yearlyInterestRateDecimal / numberOfYearlyPayments;

  const downPayment =
    PMT(nominalInterestRate, numberOfRepayments, amount + establishmentFee) -
    paymentFee;

  let prevClosingBalance = 0;
  const payments = Array.apply(0, Array(numberOfRepayments + 1)).map(
    (_, index) => {
      if (index === 0) {
        prevClosingBalance = amount + establishmentFee;
        return { calcPayment: amount };
      } else {
        const fees = paymentFee;
        const isLast = index === numberOfRepayments;

        if (loanType === LoanTypes.Annuity) {
          const _interestCost = IPMT(
            nominalInterestRate,
            index,
            numberOfRepayments,
            amount + establishmentFee
          );

          const amortization = isLast
            ? prevClosingBalance
            : Math.round(
                Math.abs(downPayment) - Math.abs(_interestCost) - fees
              );

          // Om sista betalningen

          const payment = !isLast
            ? Math.round(Math.abs(downPayment))
            : Math.round(
                prevClosingBalance + paymentFee + Math.abs(_interestCost)
              );

          const remaining = isLast ? 0 : prevClosingBalance - amortization;
          prevClosingBalance = remaining;

          return {
            calcPayment: downPayment,
            payment,
            interestCost: Math.round(Math.abs(_interestCost)),
            fees,
            amortization,
            remaining,
          };
        } else {
          const _interestCost = prevClosingBalance * nominalInterestRate;
          const amortization = Math.round(
            (amount + establishmentFee) / numberOfRepayments
          );
          const remaining = isLast ? 0 : prevClosingBalance - amortization;
          const _payment = amortization + _interestCost + fees;
          prevClosingBalance = remaining;

          return {
            calcPayment: -_payment,
            interestCost: Math.round(_interestCost),
            payment: Math.round(_payment),
            fees,
            amortization: amortization,
            remaining,
          };
        }
      }
    }
  );

  const monthlyDiscountRate = IRR(
    payments.map(({ calcPayment }) => calcPayment)
  );
  const APR = Math.pow(1 + monthlyDiscountRate, numberOfYearlyPayments) - 1;
  console.log('effectiv ränta:' +APR);
  const totalPayment =
    loanType === LoanTypes.Annuity
      ? downPayment * numberOfRepayments
      : payments.reduce(
          (acc, curr, index) =>
            index > 0 ? acc + Math.abs(curr.calcPayment) : 0,
          0
        ) + establishmentFee;

  return {
    totalAmount: Math.round(Math.abs(totalPayment)),
    monthlyPayment: Math.round(Math.abs(downPayment)),
    calculatedMonthlyFee: -Math.round(totalPayment/numberOfRepayments),
    totalCost: Math.round(Math.abs(totalPayment)) - amount,
    payments,
    
  };
};
